import { create } from "zustand";
import React from "react";
import { IGift, IGiftOption, IGiftOptions, IGiftProductCategoriesModels } from "@/types/gift";
import { IOfferzBuyOptions, IOfferzProductTabs } from "@/types/product";

type KeyErrorPair = {
  key: string;
  error: string;
};
type KeyValuePair = {
  key: string;
  value: string;
};
const defaultProduct = {
  // Properties from the Gift interface
  id: "",
  has_gift: 0,
  key: "",
  maximum: "",
  minimum: "",
  model: "",
  name: "",
  points: 0,
  price: 0,
  price_raw: 0,
  product_id: "",
  quantity: "0",
  reward: "",
  seo_url: "",
  stock: false,
  thumb: "",
  total: "0",
  total_raw: 0,
  option: [] as IGiftOption[],
  options: {} as IGiftOptions,
  original_image: "",
  special: 0,

  offerz_product_tabs: [] as IOfferzProductTabs[],
  offerz_similar_products: [] as any[],
  offerz_options: { buy_options: [] } as { buy_options: IOfferzBuyOptions[] },
  seo_urls: {} as Record<string, any>,
  subtitle: "",
  type: "",
};
const getCurrentDateTime = () => {
  const tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
  const isoString = new Date(Date.now() + 600000 - tzoffset).toISOString().slice(0, -1); //added 10 minutes extra buffer so date is always in the future
  // Convert to local datetime format by removing seconds and "Z"
  return isoString.substring(0, 16);
};

interface GiftStore {
  productId: string;
  productOption: KeyValuePair[];
  hasPriceOption: IOfferzBuyOptions;
  errors: KeyErrorPair[];
  customizeProduct: boolean;
  buttonClicked: boolean;
  product: IGift;
  priceDisplay: number;
  setPriceDisplay: (id: number) => void;
  setProductId: (id: string) => void;
  setProductOption: (option: KeyValuePair[]) => void;
  setHasPriceOption: (option: IOfferzBuyOptions) => void;
  setErrors: (errors: KeyErrorPair[]) => void;
  setErrorMessage: (key: string, message: string) => void;
  setCustomizeProduct: (customize: boolean) => void;
  setButtonClicked: (clicked: boolean) => void;
  setProduct: (product: IGift) => void;

  countryCode: string;
  deliveryDate: string;
  messageField: string;
  deliveryDateExact: string;
  finish: boolean;
  deliveryMethod: string;
  name: string;
  email: string;
  phone: string;
  open: boolean;
  submitted: boolean;
  productCard: ProductCard;
  previewUploadedFile: string;
  uploadedFile: { type: string; name: string };
  blob: Blob | null;
  scrollFromImage: boolean;
  selectedBackground: Background;

  setCountryCode: React.Dispatch<React.SetStateAction<string>>;
  setDeliveryDate: (date: string) => void;
  setMessageField: (message: string) => void;
  setDeliveryDateExact: (date: string) => void;
  setFinish: (finish: boolean) => void;
  setDeliveryMethod: (method: string) => void;
  setName: (name: string) => void;
  setEmail: (email: string) => void;
  setPhone: (phone: string) => void;
  setOpen: (open: boolean) => void;
  setSubmitted: (submitted: boolean) => void;
  setProductCard: (card: ProductCard) => void;
  setPreviewUploadedFile: (file: string) => void;
  setUploadedFile: (file: { type: string; name: string }) => void;
  setBlob: (blob: Blob | null) => void;
  setScrollFromImage: (scroll: boolean) => void;
  selectedCategory: {
    category_id: number | null;
    models: IGiftProductCategoriesModels[];
  };
  setSelectedCategory: (category: {
    category_id: number | null;
    models: IGiftProductCategoriesModels[];
  }) => void;
  setSelectedBackground: (background: Background) => void;
  reset: () => void;
}
interface ProductCard {
  src: string;
  name: string;
  id: string;
}

interface Background {
  image: string;
  model_id: string;
  angle: string;
  hex_start: string;
  hex_end: string;
}
const initialState = {
  countryCode: "41",
  deliveryDate: "",
  messageField: "",
  deliveryDateExact: getCurrentDateTime(),
  finish: false,
  deliveryMethod: "",
  name: "",
  email: "",
  phone: "",
  open: false,
  submitted: false,
  productCard: { src: "", name: "", id: "" },
  previewUploadedFile: "",
  uploadedFile: { type: "", name: "" },
  blob: null,
  scrollFromImage: false,
  selectedCategory: {
    category_id: null,
    models: [],
  },
  selectedBackground: {
    image: "",
    model_id: "",
    angle: "",
    hex_start: "",
    hex_end: "",
  },
};
export const useGiftStore = create<GiftStore>((set) => ({
  productId: "",
  productOption: [],
  hasPriceOption: {
    option_id: "",
    product_option_id: "",
    name: "",
    required: false,
    type: "",
    hidden: 0,
    conditions: { min_amount: 0, max_amount: 0 },
  },
  errors: [],
  customizeProduct: false,
  buttonClicked: false,
  priceDisplay: 0,
  product: defaultProduct,
  ...initialState,
  setPriceDisplay: (priceDisplay) => set({ priceDisplay }),
  setProductId: (id) => set({ productId: id }),
  setProductOption: (productOption: KeyValuePair[]) => set({ productOption }),
  setHasPriceOption: (option) => set({ hasPriceOption: option }),
  setErrors: (errors: KeyErrorPair[]) => set({ errors }),
  setErrorMessage: (key, message) =>
    set((state) => {
      const updated = key === "product" ? [] : state.errors.filter((err) => err.key !== key);
      return { errors: message ? [...updated, { key, error: message }] : updated };
    }),
  setCustomizeProduct: (customize) => set({ customizeProduct: customize }),
  setButtonClicked: (clicked) => set({ buttonClicked: clicked }),
  setProduct: (product) => set({ product }),

  setCountryCode: () => set((state) => ({ countryCode: state.countryCode })),
  setDeliveryDate: (date) => set({ deliveryDate: date }),
  setMessageField: (message) => set({ messageField: message }),
  setDeliveryDateExact: (date) => set({ deliveryDateExact: date }),
  setFinish: (finish) => set({ finish }),
  setDeliveryMethod: (method) => set({ deliveryMethod: method }),
  setName: (name) => set({ name }),
  setEmail: (email) => set({ email }),
  setPhone: (phone) => set({ phone }),
  setOpen: (open) => set({ open }),
  setSubmitted: (submitted) => set({ submitted }),
  setProductCard: (card) => set({ productCard: card }),
  setPreviewUploadedFile: (file) => set({ previewUploadedFile: file }),
  setUploadedFile: (file) => set({ uploadedFile: file }),
  setBlob: (blob) => set({ blob }),
  setScrollFromImage: (scroll) => set({ scrollFromImage: scroll }),
  setSelectedCategory: (category) => set({ selectedCategory: category }),
  setSelectedBackground: (background) => set({ selectedBackground: background }),
  reset: () => set(initialState),
}));
